<script lang="ts" module>
	import type { Snippet } from 'svelte';
	import type { ClassValue, SvelteHTMLElements } from 'svelte/elements';
	import type { VariantProps } from 'tailwind-variants';
	import { tv } from '../tv';
	import { MAIN_COLOR_VARIANTS, RADII_VARIANTS } from '../variants';

	export const buttonVariants = tv({
		base: 'inline-flex max-w-full min-w-s0 cursor-pointer items-center justify-center hover:bg-(--btn-hover) focus-visible:bg-(--btn-hover) active:bg-(--btn-active) aria-pressed:bg-(--btn-active) aria-pressed:hover:bg-(--btn-hover) aria-pressed:focus-visible:bg-(--btn-hover) aria-pressed:active:bg-(--btn-active) data-[ui-after=true]:pr-(--btn-ui-space) data-[ui-before=true]:pl-(--btn-ui-space) focus-outline disabled-state',
		variants: {
			size: {
				'300': 'h-[2rem] gap-s100 px-s300 [--btn-ui-space:var(--spacing-s200)]',
				'400': 'h-[2.5rem] gap-s200 px-s400 [--btn-ui-space:var(--spacing-s300)]',
				'500': 'h-[3rem] gap-s300 px-s500 [--btn-ui-space:var(--spacing-s400)]'
			},
			variant: MAIN_COLOR_VARIANTS,
			fill: {
				Solid:
					'border-(--v-main-line) bg-(--v-main) text-(--v-on-main) [--btn-active:var(--v-main-active)] [--btn-hover:var(--v-main-hover)]',
				Soft: 'border-(--v-container-line) bg-(--v-container) text-(--v-on-container) [--btn-active:var(--v-container-active)] [--btn-hover:var(--v-container-hover)]',
				None: 'border-(--v-main) bg-inherit text-(--v-main) [--btn-active:var(--v-container-active)] [--btn-hover:var(--v-container-hover)]'
			},
			outlined: { true: 'border-b400' },
			radii: RADII_VARIANTS
		},
		defaultVariants: { size: '400', variant: 'Primary', fill: 'Solid', radii: '400' }
	});

	export type ButtonProps<T extends keyof SvelteHTMLElements = 'button'> = Omit<
		SvelteHTMLElements[T],
		'class'
	> &
		VariantProps<typeof buttonVariants> & {
			after?: Snippet;
			as?: T;
			before?: Snippet;
			children?: Snippet;
			class?: ClassValue;
			ref?: HTMLElement | null;
		};
</script>

<script lang="ts" generics="T extends keyof SvelteHTMLElements = 'button'">
	let {
		after,
		as = 'button' as T,
		before,
		children,
		class: className,
		fill,
		outlined,
		radii,
		ref = $bindable(null),
		size,
		variant,
		...rest
	}: ButtonProps<T> = $props();
</script>

<svelte:element
	this={as}
	bind:this={ref}
	class={[buttonVariants({ fill, outlined, radii, size, variant }), className]}
	data-ui-after={after ? true : undefined}
	data-ui-before={before ? true : undefined}
	{...rest}
>
	{@render before?.()}{@render children?.()}{@render after?.()}
</svelte:element>
